import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppLayout } from "../components/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api } from "../lib/api";
import { Loader2, Save } from "lucide-react";
import { TestCallCard } from "../components/TestCallCard";
import { KinvantaCard } from "../components/KinvantaCard";
import { useAuth } from "../lib/auth";

export default function Settings() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/settings");
      setState({
        email_summaries: !!data.notifications?.email_summaries,
        sms_alerts: !!data.notifications?.sms_alerts,
        alert_sensitivity: data.notifications?.alert_sensitivity || "medium",
        paused: !!data.paused,
      });
      setLoading(false);
    })();
  }, []);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", state);
      toast.success("Settings saved");
    } finally { setSaving(false); }
  };

  if (loading || !state) {
    return <AppLayout><div className="grid place-items-center h-96"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></AppLayout>;
  }

  return (
    <AppLayout
      title="Settings"
      subtitle="Choose how CherishLink keeps you in the loop."
      actions={
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="settings-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
        </Button>
      }
    >
      <div className="space-y-5 max-w-2xl">
        <Card className="rounded-3xl border-border/60 soft-shadow">
          <CardContent className="p-6 md:p-8">
            <h3 className="font-display text-lg font-semibold">Notifications</h3>
            <p className="text-sm text-muted-foreground mt-1">Warm updates — never noisy.</p>
            <div className="mt-6 space-y-4">
              <ToggleRow
                testid="set-email"
                title="Email summaries" subtitle="Short recap after each call"
                checked={state.email_summaries} onChange={(v) => set("email_summaries", v)} />
              <ToggleRow
                testid="set-sms"
                title="SMS alerts" subtitle="Texts for mood dips or missed calls"
                checked={state.sms_alerts} onChange={(v) => set("sms_alerts", v)} />
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl border border-border">
                <div>
                  <div className="font-semibold">Alert sensitivity</div>
                  <div className="text-sm text-muted-foreground">How eager should we be to alert you?</div>
                </div>
                <Select value={state.alert_sensitivity} onValueChange={(v) => set("alert_sensitivity", v)}>
                  <SelectTrigger className="w-[200px] h-11 rounded-xl" data-testid="set-sensitivity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low — only serious concerns</SelectItem>
                    <SelectItem value="medium">Medium — balanced</SelectItem>
                    <SelectItem value="high">High — any mood dip</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-3xl border-border/60 soft-shadow">
          <CardContent className="p-6 md:p-8">
            <h3 className="font-display text-lg font-semibold">Service</h3>
            <div className="mt-6">
              <ToggleRow
                testid="set-paused"
                title="Pause all CherishLink calls" subtitle="No calls will be made until you turn this off."
                checked={state.paused} onChange={(v) => set("paused", v)} />
            </div>
          </CardContent>
        </Card>

        <TestCallCard
          source="settings"
          defaultPhone={user?.mobile || ""}
          defaultName={user?.name || ""}
          defaultEmail={user?.email || ""}
        />

        <KinvantaCard variant="module" source="settings" />
      </div>
    </AppLayout>
  );
}

function ToggleRow({ title, subtitle, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between p-4 rounded-2xl border border-border">
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-sm text-muted-foreground">{subtitle}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
